"use client";

import { useState, useEffect } from "react";
import useAuth from "@/hooks/useAuth";
import { useLocation } from "@/contexts/LocationContext";
import ProfileForm from "@/components/account/ProfileForm";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import MainLayout from "@/layouts/MainLayout";
import {
  User,
  Mail,
  Shield,
  Edit3,
  X,
  CheckCircle,
  AlertCircle
} from "lucide-react";
import { cn } from "@/lib/utils";
import AuthService from "@/lib/auth";

export default function Profile() {
  const { user } = useAuth();
  const { locations } = useLocation();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fullProfile, setFullProfile] = useState<any>(null);

  // Fetch full enriched profile from API when user clicks Edit
  // so that primary_doctor_name / secondary_doctor_name are available
  // on the very first render of ProfileForm — no race condition.
  const handleEditClick = async () => {
    try {
      const profile = await AuthService.getProfile(user?.id);
      setFullProfile(profile);
    } catch {
      setFullProfile(null);
    }
    setEditing(true);
  };

  useEffect(() => {
    if (user) {
      setLoading(false);
    }
  }, [user]);

  // Helper function to format role name
  const formatRoleName = (role: string | null | undefined) => {
    if (!role) return "No Role Assigned";
    return role.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
  };

  // Helper function to get user initials
  const getUserInitials = (name: string | undefined) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map(word => word.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };


  // Loading state
  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="flex flex-col items-center space-y-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <p className="text-muted-foreground">Loading your profile...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  // No user state
  if (!user) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Card className="w-full max-w-md">
            <CardContent className="pt-6">
              <div className="flex flex-col items-center space-y-4">
                <AlertCircle className="h-12 w-12 text-muted-foreground" />
                <div className="text-center">
                  <h3 className="font-semibold">Unable to Load Profile</h3>
                  <p className="text-sm text-muted-foreground">
                    Please try refreshing the page or logging in again.
                  </p>
                </div>
                <Button onClick={() => window.location.reload()}>
                  Refresh Page
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="min-h-screen bg-[#f7f8fa] -m-6 p-6 lg:p-10 font-sans">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div>
              <h1 className="text-3xl lg:text-4xl font-extrabold text-[#1a7a4a] tracking-tight">
                Profile Settings
              </h1>
              <p className="text-gray-500 mt-2 text-lg">
                Manage your account information and preferences
              </p>
            </div>
            {!editing && (
              <Button
                onClick={() => handleEditClick()}
                className="bg-[#1a7a4a] hover:bg-[#15633c] text-white rounded-2xl h-11 px-6 shadow-lg shadow-green-100 transition-all hover:scale-[1.02] flex items-center gap-2 font-semibold"
              >
                <Edit3 className="w-4 h-4" />
                Edit Profile
              </Button>
            )}
          </div>

          {/* Profile Card */}
          <Card className="rounded-3xl border-gray-100 shadow-xl shadow-gray-200/50 bg-white overflow-hidden">
            <CardHeader className="pb-8 border-b border-gray-50">
              <div className="flex items-center gap-6">
                <Avatar className="h-20 w-20 border-4 border-white shadow-xl">
                  <AvatarImage src={user.photo_url} alt={user.name} />
                  <AvatarFallback className="text-2xl font-black bg-[#1a7a4a] text-white">
                    {getUserInitials(user.name)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h2 className="text-2xl font-black text-gray-900 leading-tight">
                    {user.name || "Unnamed User"}
                  </h2>
                  <div className="flex items-center gap-3 mt-2">
                    <Badge className="bg-[#1a7a4a]/10 text-[#1a7a4a] border-none px-3 py-1 rounded-xl text-xs font-bold uppercase tracking-wider">
                      {formatRoleName(user.role)}
                    </Badge>
                    {user.email && (
                      <span className="text-sm font-medium text-gray-400 flex items-center gap-1.5">
                        <Mail className="w-3.5 h-3.5" />
                        {user.email}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </CardHeader>

            <CardContent className="p-8">
              {!editing ? (
                /* View Mode */
                <div className="space-y-10">
                  {/* Basic Information */}
                  <div className="space-y-6">
                    <h3 className="text-lg font-extrabold text-[#1a7a4a] flex items-center gap-2">
                      <div className="w-8 h-8 rounded-xl bg-[#1a7a4a]/10 flex items-center justify-center">
                        <User className="h-4 w-4" />
                      </div>
                      Basic Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="space-y-2">
                        <label className="text-xs font-black text-gray-400 uppercase tracking-widest px-1">
                          Full Name
                        </label>
                        <div className="p-4 bg-gray-50/50 rounded-2xl border border-gray-50 text-gray-900 font-bold">
                          {user.name || "Not provided"}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="text-xs font-black text-gray-400 uppercase tracking-widest px-1">
                          Email Address
                        </label>
                        <div className="p-4 bg-gray-50/50 rounded-2xl border border-gray-50 text-gray-900 font-bold flex items-center gap-3">
                          <Mail className="h-4 w-4 text-[#1a7a4a]" />
                          {user.email || "Not provided"}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="text-xs font-black text-gray-400 uppercase tracking-widest px-1">
                          Role & Permissions
                        </label>
                        <div className="p-4 bg-gray-50/50 rounded-2xl border border-gray-50 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Shield className="h-4 w-4 text-[#1a7a4a]" />
                            <span className="font-bold text-gray-900">
                              {formatRoleName(user.role)}
                            </span>
                          </div>
                          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-100 font-bold rounded-lg px-2 py-0.5 text-[10px] uppercase tracking-wider">
                            {user.role ? "Active" : "Pending"}
                          </Badge>
                        </div>
                      </div>

                      {user.associated_branch && (
                        <div className="space-y-2">
                          <label className="text-xs font-black text-gray-400 uppercase tracking-widest px-1">
                            Main Associated Branch
                          </label>
                          <div className="p-4 bg-gray-50/50 rounded-2xl border border-gray-50 flex items-center gap-3">
                            <CheckCircle className="h-4 w-4 text-[#1a7a4a]" />
                            <span className="font-bold text-gray-900 truncate">
                              {user.associated_branch_name
                                || user.associated_state_name
                                || locations.find(l => String(l.value) === String(user.associated_branch))?.label?.split(' — ')[1]
                                || locations.find(l => String(l.value) === String(user.associated_branch))?.label
                                || user.associated_branch}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <Separator className="bg-gray-100" />

                  {/* Account Status */}
                  <div className="space-y-6">
                    <h3 className="text-lg font-extrabold text-[#1a7a4a] flex items-center gap-2">
                      <div className="w-8 h-8 rounded-xl bg-[#1a7a4a]/10 flex items-center justify-center">
                        <CheckCircle className="h-4 w-4" />
                      </div>
                      Account Status
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div className="p-5 border border-gray-100 rounded-3xl bg-gray-50/30 hover:bg-white hover:shadow-xl hover:shadow-gray-200/40 transition-all group">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-black text-gray-900">
                            Profile Completion
                          </span>
                          <Badge className="bg-emerald-50 text-emerald-700 border-none font-black text-[10px]">
                            COMPLETE
                          </Badge>
                        </div>
                        <p className="text-xs text-gray-500 font-medium">
                          Your profile information is fully up to date.
                        </p>
                      </div>

                      <div className="p-5 border border-gray-100 rounded-3xl bg-gray-50/30 hover:bg-white hover:shadow-xl hover:shadow-gray-200/40 transition-all group">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-black text-gray-900">
                            Email Verification
                          </span>
                          <Badge
                            className={cn(
                              "border-none font-black text-[10px]",
                              user.email
                                ? "bg-blue-50 text-blue-700"
                                : "bg-amber-50 text-amber-700"
                            )}
                          >
                            {user.email ? "VERIFIED" : "PENDING"}
                          </Badge>
                        </div>
                        <p className="text-xs text-gray-500 font-medium">
                          {user.email
                            ? "Confirmed and ready for communication."
                            : "Check your inbox to verify your email."}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* Edit Mode */
                <div className="space-y-8">
                  <div className="p-6 bg-[#1a7a4a]/5 border border-[#1a7a4a]/10 rounded-3xl flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-[#1a7a4a]/10 flex items-center justify-center">
                      <Edit3 className="h-6 w-6 text-[#1a7a4a]" />
                    </div>
                    <div>
                      <h4 className="font-extrabold text-[#1a7a4a] text-lg">
                        Editing Profile
                      </h4>
                      <p className="text-gray-500 font-medium text-sm">
                        Keep your professional information current for patients and staff.
                      </p>
                    </div>
                  </div>

                  <ProfileForm
                    defaultValues={{
                      name: fullProfile?.name || fullProfile?.full_name || user.name || "",
                      email: fullProfile?.email || user.email || "",
                      role: fullProfile?.role || user.role || "",
                      npi: fullProfile?.npi || "",
                      primary_doctor_associated: fullProfile?.primary_doctor_associated || "",
                      secondary_doctor_associated: fullProfile?.secondary_doctor_associated || "",
                      primary_doctor_name: fullProfile?.primary_doctor_name || "",
                      secondary_doctor_name: fullProfile?.secondary_doctor_name || "",
                      associated_branch: fullProfile?.associated_branch || "",
                      location_ids: fullProfile?.location_ids || "",
                    }}
                  />

                  <div className="flex justify-end gap-3 pt-6 border-t border-gray-100">
                    <Button
                      variant="ghost"
                      onClick={() => setEditing(false)}
                      className="rounded-2xl h-11 px-6 text-gray-500 font-bold hover:bg-gray-100 transition-all"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Cancel Changes
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Additional Information */}
          <div className="text-center pt-4">
            <p className="text-sm text-gray-400 font-medium">
              Need assistance?{" "}
              <Button
                variant="link"
                className="p-0 h-auto text-sm font-black text-[#1a7a4a] hover:no-underline"
                onClick={() => {
                  window.location.href = "mailto:admin@monitorhealth.ai?subject=Support%20Request%20-%20Monitor%20Health%20AI&body=Hello%20Support%20Team,%0D%0A%0D%0AI%20need%20assistance%20with%20the%20following%20issue:%0D%0A";
                }}
              >
                Contact Support Team
              </Button>
            </p>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}